'use client'

import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'

import type { IDotColorProps } from '~/core/ui/Dot'
import { Dot } from '~/core/ui/Dot'
import {
  CheckFill,
  CircleCheckFill,
  CircleCheckLine,
  ClockIcon,
  DotIcon,
  NoFeedbackIcon,
  PendingFeedbackIcon,
  StrongNoFeedbackIcon,
  StrongYesFeedbackIcon,
  XCircleIcon,
  XIcon,
  YesFeedbackIcon
} from '~/core/ui/FillIcons'
import type { LucideIconName } from '~/core/ui/IconWrapper'
import IconWrapper from '~/core/ui/IconWrapper'
import { cn } from '~/core/ui/utils'
import { isHTML } from '~/core/utilities/common'

const badgeNotificationVariant = cva('', {
  variants: {
    color: {
      green: 'bg-green-500 dark:bg-green-800 dark:text-green-100',
      red: 'border border-solid border-white bg-red-500 dark:bg-red-800 dark:text-red-100'
    },
    size: {
      lg: 'h-3.5',
      md: 'h-3.5',
      sm: 'h-3.5',
      xs: 'h-4'
    }
  }
})
const badgeNotificationTextVariant = cva('', {
  variants: {
    size: {
      lg: 'text-sm',
      md: 'text-xs',
      sm: 'text-xs',
      xs: 'text-3xs'
    }
  }
})
const badgeVariants = cva('flex items-center justify-center', {
  variants: {
    color: {
      green:
        'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
      pastelGreen:
        'bg-pastel-green-50 dark:bg-pastel-green-1000 text-pastel-green-1000 dark:text-pastel-green-100',
      purple:
        'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100',
      yellow:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100',
      orange:
        'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100',
      red: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100',
      'dark-blue':
        'bg-blue-1000 text-blue-1200 dark:bg-blue-800 dark:text-blue-100',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-100',
      white: 'bg-white text-gray-700 dark:bg-gray-600 dark:text-gray-100',
      'chart-sourced': 'bg-blue-100 text-blue-800',
      'chart-applied': 'bg-purple-100 text-purple-800',
      'chart-screening': 'bg-red-100 text-red-800',
      'chart-client-submission': 'bg-orange-100 text-orange-800',
      'chart-clientSubmission': 'bg-orange-100 text-orange-800',
      'chart-interview': 'bg-yellow-100 text-yellow-800',
      'chart-offer': 'text-pastel-green-800 bg-pastel-green-100',
      'chart-hired': 'bg-green-100 text-green-800',
      'chart-onboard': 'bg-green-100 text-green-800',
      'chart-probation': 'bg-green-100 text-green-800',
      'matched-rank-s': 'bg-green-500 text-white',
      'matched-rank-a': 'bg-chart-200 text-white',
      'matched-rank-b': 'bg-chart-400 text-white',
      'matched-rank-c': 'bg-chart-900 text-white'
    },
    radius: {
      rounded: 'rounded-sm',
      circular: 'rounded-xl'
    },
    size: {
      sm: 'h-4 px-1.5 py-0.5',
      md: 'h-5 px-1.5 py-1',
      lg: 'h-6 px-2 py-0.5',
      xs: 'h-4 px-1 py-0.5'
    },
    type: {
      default: '',
      dotLeading: '',
      iconLeading: 'flex-row',
      trailingIcon: 'flex-row-reverse',
      notification: ''
    },
    variant: {
      default: '',
      outline:
        'border border-gray-100 bg-inherit text-gray-700 dark:border-gray-700'
    }
  },
  defaultVariants: {
    color: 'gray',
    radius: 'rounded',
    size: 'md',
    type: 'default',
    variant: 'default'
  }
})

const badgeFontVariants = cva('', {
  variants: {
    size: {
      lg: 'text-sm',
      md: 'text-xs',
      sm: 'text-xs',
      xs: 'text-xs'
    },
    variant: {
      default: 'font-medium',
      outline: 'font-normal'
    }
  },
  defaultVariants: {
    size: 'md',
    variant: 'default'
  }
})

const getSizeIconBadges = {
  lg: 14,
  md: 12,
  sm: 12,
  xs: 10
}

type IColorBadgeType =
  | 'green'
  | 'pastelGreen'
  | 'purple'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'blue'
  | 'gray'
  | 'white'
  | 'chart-sourced'
  | 'chart-applied'
  | 'chart-screening'
  | 'chart-client-submission'
  | 'chart-clientSubmission'
  | 'chart-interview'
  | 'chart-offer'
  | 'chart-hired'
  | 'chart-onboard'
  | 'chart-probation'
  | 'matched-rank-s'
  | 'matched-rank-a'
  | 'matched-rank-b'
  | 'matched-rank-c'

interface BadgeProps {
  ref?: React.Ref<HTMLSpanElement>
  type?:
    | 'default'
    | 'dotLeading'
    | 'iconLeading'
    | 'trailingIcon'
    | 'notification'
  radius?: 'rounded' | 'circular'
  color?: IColorBadgeType
  dotColor?: IDotColorProps
  size?: 'sm' | 'md' | 'lg' | 'xs'
  badgeCount?: number
  children?: ReactNode
  icon?: LucideIconName
  iconSVG?: LucideIconName | 'CheckCircleLine' | 'QuestionMarkCircle'
  variant?: 'default' | 'outline'
  className?: string
  classNameText?: string
  classNameIcon?: string
  classNameIconSVG?: string
  startPlus?: number
}

const Badge = ({
  ref,
  children,
  type = 'default',
  radius = 'rounded',
  color = 'gray',
  dotColor,
  size = 'md',
  badgeCount: badgeCountNonDefault,
  icon,
  iconSVG,
  variant = 'default',
  className = '',
  classNameText = '',
  classNameIcon = '',
  classNameIconSVG = '',
  startPlus = 10
}: BadgeProps) => {
  const badgeCount = badgeCountNonDefault || 0
  const renderSVGIcon = () => {
    let icon = undefined

    switch (iconSVG) {
      case 'Ban':
        icon = <StrongNoFeedbackIcon className={classNameIconSVG} />
        break
      case 'ThumbsDown':
        icon = <NoFeedbackIcon className={classNameIconSVG} />
        break
      case 'ThumbsUp':
        icon = <YesFeedbackIcon className={classNameIconSVG} />
        break
      case 'CheckCircle':
        icon = (
          <CircleCheckFill
            className={`fill-green-500! ${classNameIconSVG}`}
            size={12}
          />
        )
        break
      case 'CheckCircleLine':
        icon = <CircleCheckLine />
        break
      case 'Clock':
        icon = <ClockIcon className={classNameIconSVG} />
        break
      case 'Check':
        icon = <CheckFill className={classNameIconSVG} />
        break
      case 'X':
        icon = <XIcon className={classNameIconSVG} />
        break
      case 'XCircle':
        icon = <XCircleIcon className={classNameIconSVG} />
        break
      case 'Dot':
        icon = <DotIcon className={classNameIconSVG} />
        break
      case 'QuestionMarkCircle':
        icon = <PendingFeedbackIcon />
        break
      default:
        icon = <StrongYesFeedbackIcon className={classNameIconSVG} />
        break
    }

    return icon
  }

  if (type === 'notification') {
    return (
      <span
        ref={ref}
        className={cn(
          'flex min-w-[17px] items-center justify-center rounded-xl px-[5px] text-white',
          badgeNotificationVariant({
            color: color === 'green' ? 'green' : 'red',
            size: size
          })
        )}>
        <span
          className={cn('font-medium', badgeNotificationTextVariant({ size }))}>
          {badgeCountNonDefault !== undefined
            ? badgeCount > startPlus
              ? `${startPlus}+`
              : badgeCount
            : children}
        </span>
      </span>
    )
  }

  return (
    <span
      ref={ref}
      className={cn(
        badgeVariants({
          color,
          radius,
          size,
          type,
          variant
        }),
        className
      )}>
      {icon && type === 'iconLeading' ? (
        <span
          className={
            children ? (size === 'lg' ? 'mr-[6px]' : 'mr-1') : undefined
          }>
          <IconWrapper
            className={classNameIcon}
            name={icon}
            size={getSizeIconBadges[size]}
          />
        </span>
      ) : null}
      {iconSVG && type === 'iconLeading' ? (
        <span
          className={
            children ? (size === 'lg' ? 'mr-[6px]' : 'mr-1') : undefined
          }>
          {renderSVGIcon()}
        </span>
      ) : null}
      {icon && type === 'trailingIcon' ? (
        <span
          className={
            children ? (size === 'lg' ? 'ml-[6px]' : 'ml-1') : undefined
          }>
          <IconWrapper
            className={classNameIcon}
            name={icon}
            size={getSizeIconBadges[size]}
          />
        </span>
      ) : null}
      {iconSVG && type === 'trailingIcon' ? (
        <span
          className={
            children ? (size === 'lg' ? 'ml-[6px]' : 'ml-1') : undefined
          }>
          {renderSVGIcon()}
        </span>
      ) : null}
      {type === 'dotLeading' ? (
        <Dot
          color={dotColor || color}
          size="xl"
          className={cn(
            'flex-none',
            children ? (size === 'lg' ? 'mr-[6px]' : 'mr-1') : undefined
          )}
        />
      ) : null}
      {typeof children === 'string' && isHTML(children) ? (
        <span
          className={cn(
            badgeFontVariants({ size, variant, className: classNameText })
          )}
          dangerouslySetInnerHTML={{ __html: children }}
        />
      ) : (
        <span
          className={cn(
            badgeFontVariants({ size, variant, className: classNameText })
          )}>
          {children}
        </span>
      )}
    </span>
  )
}
Badge.displayName = 'Badge'

export { Badge }
export type { BadgeProps, IColorBadgeType }
